/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import type {EagColumn} from '../../../../../types/EagColumn';
import {matchesColumnSearch} from '../../../../../utils/matchesColumnSearch';
import {normalizeSearchText} from '../../../../../utils/normalizeSearchText';

/**
 * Returns the keys of columns whose headerName or group label contains the
 * search term (case- and diacritics-insensitive). An empty term matches
 * nothing — the panel treats "no search" as "no match marks".
 */
export const getMatchedColumnKeys = (columns: EagColumn[], searchTerm: string): Set<string> => {
  const normalized = normalizeSearchText(searchTerm);
  if (!normalized) {
    return new Set();
  }
  return new Set(
    columns
      .filter((column) => matchesColumnSearch(column.headerName, column.group, normalized))
      .map((column) => column.key)
  );
};

// Match strength, best first: exact name, name prefix, name substring,
// group-label substring, no match.
const matchRank = (column: EagColumn, normalizedTerm: string): number => {
  const normalizedName = normalizeSearchText(column.headerName);
  const normalizedGroup = isNotNil(column.group) ? normalizeSearchText(column.group) : '';
  return match({normalizedName, normalizedGroup})
    .with({normalizedName: normalizedTerm}, () => 0)
    .when(
      ({normalizedName: name}) => name.startsWith(normalizedTerm),
      () => 1
    )
    .when(
      ({normalizedName: name}) => name.includes(normalizedTerm),
      () => 2
    )
    .when(
      ({normalizedGroup: group}) => group.includes(normalizedTerm),
      () => 3
    )
    .otherwise(() => Number.POSITIVE_INFINITY);
};

/**
 * Returns the keys of every match for the term among `columns` (given in
 * display order), strongest first: exact name beats prefix beats substring
 * beats a group-label match, display order breaks ties within a tier. So
 * typing "note" ranks a "Note" column first even when "Reservation note"
 * appears earlier in the list, and typing a group name lists that group's
 * columns after any column actually named like the term. Comparison is case-
 * and diacritics-insensitive.
 */
export const getRankedMatchKeys = (columns: EagColumn[], searchTerm: string): string[] => {
  const normalized = normalizeSearchText(searchTerm);
  if (!normalized) {
    return [];
  }
  return columns
    .map((column, index) => ({
      key: column.key,
      rank: matchRank(column, normalized),
      index,
    }))
    .filter((entry) => entry.rank !== Number.POSITIVE_INFINITY)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.key);
};
